import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Container,
  useScrollTrigger,
  Slide,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
  ListItemIcon,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

import { useThemeContext } from '../context/ThemeContext';

// Hide app bar on scroll down
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// Change navigation items to include Home and Resources
const navItems = [
  { name: 'Home', path: '/', icon: <HomeIcon /> },
  { name: 'Resources', path: '/resources', icon: <DescriptionIcon /> },
];

function Header() {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeContext();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isHomePage = location.pathname === '/';
  const isPostDetailPage = location.pathname.match(/^\/blog\/[^/]+$/);
  const isLoginPage = location.pathname === '/secure-portal-access';
  const isResourcesPage = location.pathname === '/resources';
  const isPrivacyPage = location.pathname === '/privacy';
  const isTermsPage = location.pathname === '/terms';
  const isContactPage = location.pathname === '/contact';

  const shouldShowHeader = isHomePage || isPostDetailPage || isLoginPage || isResourcesPage || isPrivacyPage || isTermsPage || isContactPage;

  if (!shouldShowHeader) {
    return null;
  }

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 280, height: '100%', bgcolor: mode === 'light' ? '#f3f5f9' : '#0f172a' }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2.5,
        bgcolor: mode === 'light' ? '#ffffff' : '#1e293b',
        color: mode === 'light' ? '#111827' : '#f8fafc',
        borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: '#4F46E5',
              color: '#ffffff',
              width: 36,
              height: 36,
              mr: 1.5,
              border: 'none',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
            }}
          >
            <SchoolIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" component="div" fontWeight="bold" sx={{
            fontSize: '1.1rem',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}>
            WorldWise Scholar
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} sx={{
          color: 'text.secondary',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
        }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{
        height: '1px',
        bgcolor: 'rgba(0,0,0,0.05)'
      }} />

      <List sx={{ pt: 2.5, px: 1 }}>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.name}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1.8,
              color: location.pathname === item.path ? '#1976d2' : 'text.primary',
              bgcolor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              mx: 1.5,
              borderRadius: 2,
              mb: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(5px)'
              }
            }}
          >
            <ListItemIcon sx={{
              color: location.pathname === item.path ? '#1976d2' : 'text.secondary',
              minWidth: 40
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 'bold' : 'medium',
                fontSize: '1rem',
                color: location.pathname === item.path ? '#1976d2' : 'inherit',
              }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mt: 2, mx: 2 }} />

      <Box sx={{ p: 3, mt: 'auto' }}>

      </Box>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          color="primary"
          elevation={0}
          sx={{
            bgcolor: mode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`,
            transition: 'all 0.3s ease',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{
              display: 'flex',
              justifyContent: 'space-between',
              minHeight: { xs: 70, md: 80 },
              py: 1
            }}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'transform 0.3s ease',
                  flexGrow: 0,
                  mr: 2,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#4F46E5',
                    color: '#ffffff',
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    mr: 1.5,
                    display: 'flex',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    border: 'none'
                  }}
                >
                  <SchoolIcon fontSize="medium" />
                </Avatar>
                {/* Make the logo text more visible */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background: mode === 'light'
                      ? 'linear-gradient(to right, #111827 0%, #4F46E5 100%)'
                      : 'linear-gradient(to right, #F8FAFC 0%, #818CF8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textDecoration: 'none',
                    letterSpacing: '-0.5px',
                    display: { xs: 'none', sm: 'block' },
                    fontSize: { sm: '1.3rem', md: '1.5rem' },
                    ml: 1
                  }}
                >
                  WorldWise Scholar
                </Typography>
              </Box>

              {/* Desktop Navigation */}
              {isDesktop ? (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexGrow: 1
                }}>
                  <Box sx={{ ml: 4 }}>
                    {navItems.map((item) => (
                      <Button
                        key={item.name}
                        component={RouterLink}
                        to={item.path}
                        startIcon={item.icon}
                        sx={{
                          mx: 0.5,
                          px: 2,
                          py: 1,
                          color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                          position: 'relative',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                          fontWeight: location.pathname === item.path ? 700 : 500,
                          backgroundColor: location.pathname === item.path ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(79, 70, 229, 0.04)',
                            color: 'primary.main',
                          }
                        }}
                      >
                        {item.name}
                      </Button>
                    ))}
                  </Box>

                  {/* Action buttons - theme toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <IconButton
                      onClick={toggleTheme}
                      color="inherit"
                      sx={{
                        bgcolor: mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                        '&:hover': { bgcolor: mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }
                      }}
                    >
                      {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                // Make mobile WWS text and menu icon more visible
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={toggleTheme}
                    color="primary"
                    sx={{
                      bgcolor: mode === 'light' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: mode === 'light' ? 'primary.main' : '#818CF8',
                      letterSpacing: '-0.5px',
                      fontSize: '1.2rem'
                    }}
                  >
                    WWS
                  </Typography>
                  <IconButton
                    color="primary"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{
                      ml: 1,
                      bgcolor: mode === 'light' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: mode === 'light' ? 'rgba(79, 70, 229, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll >

      {/* Mobile Drawer */}
      < Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }
        }
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          '& .MuiBackdrop-root': { backgroundColor: 'rgba(18, 27, 51, 0.7)' }
        }}
      >
        {drawer}
      </Drawer >

      {/* Toolbar placeholder to prevent content from hiding behind AppBar */}
      < Toolbar />
    </>
  );
}

export default Header;